import { useParams, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useProgress } from '@/context/ProgressContext';
import { getItemById, getAllItems } from '@/data';
import { SECTIONS } from '@/data/sections';
import CodeBlock from '@/components/CodeBlock';
import { BrowserPreview, VSCodeTask, ChallengeBlock } from '@/components/LessonBlocks';
import InteractiveColorPicker from '@/components/InteractiveColorPicker';
import BoxModelVisualizer from '@/components/BoxModelVisualizer';
import FlexboxPlayground from '@/components/FlexboxPlayground';
import { ArrowLeft, ArrowRight, CheckCircle2, Star, Lock, Map } from 'lucide-react';

export default function LessonView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isCompleted, isUnlocked, completeItem } = useProgress();
  const itemId = parseInt(id);
  const item = getItemById(itemId);
  const allItems = getAllItems();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [itemId]);

  if (!item) {
    return (
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center p-8">
        <p className="text-[#A1A1AA] text-lg font-quicksand">Lesson not found</p>
        <button onClick={() => navigate('/map')} className="mt-4 text-[#1E90FF] hover:underline" data-testid="go-to-map">
          Go to Course Map
        </button>
      </div>
    );
  }

  if (!isUnlocked(itemId)) {
    return (
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center p-8">
        <Lock size={48} className="text-[#71717A] mb-4" />
        <p className="text-[#A1A1AA] text-lg font-quicksand mb-4">Complete the previous lesson first!</p>
        <button
          onClick={() => navigate('/map')}
          className="text-[#1E90FF] hover:underline flex items-center gap-2"
          data-testid="go-to-map-locked"
        >
          <Map size={16} /> Go to Course Map
        </button>
      </div>
    );
  }

  const section = SECTIONS.find(s => s.id === item.sectionId);
  const completed = isCompleted(itemId);
  const prevItem = allItems.find(i => i.id === itemId - 1);
  const nextItem = allItems.find(i => i.id === itemId + 1);

  const handleMarkDone = () => {
    if (!completed) {
      completeItem(itemId, item.xp);
    }
  };

  const renderBlock = (block, idx) => {
    const delay = `${Math.min(idx * 0.04, 0.5)}s`;
    switch (block.type) {
      case 'text':
        return (
          <p key={idx} className="text-[#E4E4E7] font-quicksand leading-relaxed mb-4 fade-in-up" style={{ animationDelay: delay }}>
            {block.value}
          </p>
        );
      case 'heading':
        return (
          <h3 key={idx} className="text-xl font-bold font-outfit text-white mt-6 mb-3 fade-in-up" style={{ animationDelay: delay }}>
            {block.value}
          </h3>
        );
      case 'hook':
        return (
          <div key={idx} className="bg-gradient-to-r from-[#FF0099]/10 to-transparent border-l-4 border-[#FF0099] p-4 rounded-r-xl mb-6 fade-in-up" style={{ animationDelay: delay }}>
            <p className="text-white font-quicksand italic leading-relaxed">{block.value}</p>
          </div>
        );
      case 'code':
        return (
          <div key={idx} className="fade-in-up" style={{ animationDelay: delay }}>
            <CodeBlock code={block.code} lang={block.lang} title={block.title} />
          </div>
        );
      case 'preview':
        return (
          <div key={idx} className="fade-in-up" style={{ animationDelay: delay }}>
            <BrowserPreview html={block.html} title={block.title} css={block.css} />
          </div>
        );
      case 'comparison':
        return (
          <div key={idx} className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4 fade-in-up" style={{ animationDelay: delay }}>
            <BrowserPreview html={block.before.html} title={block.before.label || 'Before'} />
            <BrowserPreview html={block.after.html} title={block.after.label || 'After'} css={block.after.css} />
          </div>
        );
      case 'tip':
        return (
          <div key={idx} className="bg-[#1E90FF]/10 border border-[#1E90FF]/30 rounded-xl p-4 mb-4 fade-in-up" style={{ animationDelay: delay }}>
            <p className="text-[#E4E4E7] text-sm font-quicksand">
              <span className="text-[#1E90FF] font-bold">Tip: </span>{block.value}
            </p>
          </div>
        );
      case 'warning':
        return (
          <div key={idx} className="bg-[#FF5E00]/10 border border-[#FF5E00]/30 rounded-xl p-4 mb-4 fade-in-up" style={{ animationDelay: delay }}>
            <p className="text-[#E4E4E7] text-sm font-quicksand">
              <span className="text-[#FF5E00] font-bold">Important: </span>{block.value}
            </p>
          </div>
        );
      case 'list':
        return (
          <ul key={idx} className={`${block.ordered ? 'list-decimal' : 'list-disc'} list-inside text-[#E4E4E7] mb-4 space-y-1 font-quicksand text-sm fade-in-up ml-2`} style={{ animationDelay: delay }}>
            {block.items.map((li, i) => <li key={i}>{li}</li>)}
          </ul>
        );
      case 'interactive':
        if (block.component === 'ColorPicker') return <div key={idx} className="fade-in-up" style={{ animationDelay: delay }}><InteractiveColorPicker /></div>;
        if (block.component === 'BoxModel') return <div key={idx} className="fade-in-up" style={{ animationDelay: delay }}><BoxModelVisualizer /></div>;
        if (block.component === 'FlexboxPlayground') return <div key={idx} className="fade-in-up" style={{ animationDelay: delay }}><FlexboxPlayground /></div>;
        return null;
      default:
        return null;
    }
  };

  return (
    <div className="relative z-10 min-h-screen max-w-3xl mx-auto px-4 py-6" data-testid="lesson-view">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/map')}
          className="text-[#A1A1AA] hover:text-white flex items-center gap-1 text-sm transition-colors"
          data-testid="back-to-map"
        >
          <ArrowLeft size={16} /> Course Map
        </button>
        <div className="flex items-center gap-2">
          <Star size={14} className="text-[#FFED00]" />
          <span className="text-sm font-bold text-[#FFED00] font-outfit">{item.xp} XP</span>
        </div>
      </div>

      {/* Banner */}
      <div className="bg-[#121225]/80 border border-white/10 rounded-2xl p-6 mb-8 text-center fade-in-up backdrop-blur-sm">
        <span className="text-xs text-[#71717A] font-mono uppercase tracking-wider">
          {section?.title} &mdash; {item.type === 'mini-project' ? 'Mini Project' : `Lesson`}
        </span>
        <h1 className="text-2xl md:text-3xl font-bold font-outfit text-white mt-2">{item.title}</h1>
        {item.subtitle && (
          <p className="text-[#A1A1AA] mt-1 font-quicksand text-base">{item.subtitle}</p>
        )}
        {completed && (
          <div className="mt-3 inline-flex items-center gap-1 text-[#16FF00] text-sm font-quicksand">
            <CheckCircle2 size={16} /> Completed
          </div>
        )}
      </div>

      {/* Content blocks */}
      <div className="mb-8">
        {item.content && item.content.map((block, idx) => renderBlock(block, idx))}
      </div>

      {/* VS Code Task */}
      {item.task && <VSCodeTask task={item.task} />}

      {/* Challenge */}
      {item.challenge && <ChallengeBlock challenge={item.challenge} />}

      {/* Mini Project Checklist */}
      {item.type === 'mini-project' && item.requirements && (
        <div className="bg-[#FFED00]/10 border border-[#FFED00]/30 rounded-2xl p-6 mb-6" data-testid="project-checklist">
          <h3 className="text-lg font-bold font-outfit text-[#FFED00] mb-4">Requirements Checklist</h3>
          <div className="space-y-3">
            {item.requirements.map((req, i) => (
              <label key={i} className="flex items-start gap-3 cursor-pointer group">
                <input type="checkbox" className="mt-1 accent-[#16FF00] w-4 h-4" data-testid={`checklist-${i}`} />
                <span className="text-[#E4E4E7] text-sm font-quicksand group-hover:text-white transition-colors">{req}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8 pb-12 gap-2">
        <button
          onClick={() => prevItem && navigate(`/lesson/${prevItem.id}`)}
          disabled={!prevItem}
          className={`flex items-center gap-2 px-4 py-2 rounded-full border font-outfit text-sm transition-all ${
            prevItem ? 'border-white/20 text-white hover:bg-white/10' : 'border-white/5 text-[#71717A] cursor-not-allowed'
          }`}
          data-testid="prev-lesson-btn"
        >
          <ArrowLeft size={14} /> Prev
        </button>

        {!completed ? (
          <button
            onClick={handleMarkDone}
            className="bg-[#FF0099] hover:bg-[#D60080] text-white rounded-full px-6 py-3 font-bold font-outfit shadow-[0_0_15px_rgba(255,0,153,0.3)] transition-all hover:scale-105 active:scale-95 pulse-glow"
            data-testid="mark-done-btn"
          >
            {item.type === 'mini-project' ? 'My Page Is Ready!' : 'Mark as Done'}
          </button>
        ) : (
          <span className="text-[#16FF00] font-bold flex items-center gap-2 font-outfit" data-testid="done-badge">
            <CheckCircle2 size={18} /> Done!
          </span>
        )}

        <button
          onClick={() => {
            if (nextItem && (isUnlocked(nextItem.id) || isCompleted(itemId))) {
              navigate(`/lesson/${nextItem.id}`);
            }
          }}
          disabled={!nextItem}
          className={`flex items-center gap-2 px-4 py-2 rounded-full border font-outfit text-sm transition-all ${
            nextItem ? 'border-white/20 text-white hover:bg-white/10' : 'border-white/5 text-[#71717A] cursor-not-allowed'
          }`}
          data-testid="next-lesson-btn"
        >
          Next <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}
